// Automatic market switch by IP country
// Europe -> GB market (GBP)
// Non-Europe -> detected visitor country market
// Uses the theme's native Shopify localization form so Shopify Markets
// performs server-side pricing exactly like manual country selection.

(function() {
  'use strict';

  // European country codes that should be redirected to GBP
  const EUROPEAN_COUNTRIES = [
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
    'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES',
    'SE', 'GB', 'CH', 'NO'
  ];

  // Target market country for European visitors
  const EUROPE_TARGET_COUNTRY = 'GB';

  function isLocalDevHost() {
    const host = window.location.hostname;
    return host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0';
  }

  /**
   * Returns the current Shopify country ISO code injected by the Liquid snippet
   * via the data-current-country attribute on <html>.
   */
  function getCurrentShopifyCountry() {
    const cc = document.documentElement.getAttribute('data-current-country') || '';
    console.log('[GBP Auto-Switch] Current Shopify country:', cc);
    return cc;
  }

  /**
   * Resolve the visitor IP country.
   *
   * Order of attempts:
   * 1) Shopify browsing context endpoint (same-origin, most reliable)
   * 2) Legacy Shopify CDN geolocation endpoint (fallback)
   */
  async function detectVisitorCountry() {
    const cached = sessionStorage.getItem('fmi_visitor_country');

    const tryRequests = [
      {
        name: 'browsing_context_suggestions',
        url: '/browsing_context_suggestions.json?country[enabled]=true',
        extract(data) {
          return (
            data?.detected_values?.country?.handle ||
            data?.detected_values?.country?.iso_code ||
            null
          );
        }
      },
      {
        name: 'legacy_cdn_geolocation',
        url: 'https://cdn.shopify.com/s/cdn/shop/t/3/geolocation.json',
        extract(data) {
          return data?.country?.iso_code || null;
        }
      }
    ];

    for (let i = 0; i < tryRequests.length; i += 1) {
      const req = tryRequests[i];
      try {
        const res = await fetch(req.url, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });

        if (!res.ok) {
          console.warn('[GBP Auto-Switch] Geolocation source failed:', req.name, res.status);
          continue;
        }

        const data = await res.json();
        const country = (req.extract(data) || '').toUpperCase();
        console.log('[GBP Auto-Switch] Visitor country from', req.name + ':', country, data);

        if (country) {
          sessionStorage.setItem('fmi_visitor_country', country);
          return country;
        }
      } catch (e) {
        console.warn('[GBP Auto-Switch] Geolocation source error:', req.name, e);
      }
    }

    if (cached) {
      console.log('[GBP Auto-Switch] Using cached visitor country fallback:', cached);
      return cached;
    }

    return null;
  }

  function getTargetCountry(visitorCountry) {
    if (EUROPEAN_COUNTRIES.includes(visitorCountry)) {
      return EUROPE_TARGET_COUNTRY;
    }

    return visitorCountry;
  }

  /**
   * Finds the Shopify localization <form> and its hidden country_code input,
   * sets the value to target country, then submits — exactly as the Disclosure
   * component does when a visitor manually picks a country from the selector.
   */
  function submitLocalizationForm(targetCountry) {
    // There may be multiple forms (header + footer + drawer).
    // Prefer any localization form that contains a country_code input.
    let form = null;
    const localizationForms = document.querySelectorAll('form[action="/localization"]');
    for (let i = 0; i < localizationForms.length; i += 1) {
      if (localizationForms[i].querySelector('[name="country_code"]')) {
        form = localizationForms[i];
        break;
      }
    }

    if (!form) {
      form = document.querySelector('form[action="/localization"]') || document.querySelector('.disclosure-form');
    }
    if (!form) {
      console.warn('[GBP Auto-Switch] Localization form not found in DOM.');
      return false;
    }

    const countryInput = form.querySelector('[name="country_code"]');
    if (!countryInput) {
      console.warn('[GBP Auto-Switch] country_code input not found.');
      return false;
    }

    // Guard: target country must actually be available in this store's Markets setup
    const option = form.querySelector(`[data-disclosure-option][data-value="${targetCountry}"]`);
    if (!option) {
      console.warn('[GBP Auto-Switch] Target country is not available in this store:', targetCountry);
      return false;
    }

    console.log('[GBP Auto-Switch] Submitting localization form for', targetCountry + '...');
    countryInput.value = targetCountry;
    form.submit();
    return true;
  }

  /**
   * Main entry point.
   * Runs after the DOM is ready so the localization form is present.
   */
  async function init() {
    // 0. Shopify localization POST can return 401 on local proxy URLs
    // (localhost/127.0.0.1). Skip in local dev and run on real storefront
    // domains (myshopify.com/custom domain) where Markets/localization works.
    if (isLocalDevHost()) {
      console.log('[GBP Auto-Switch] Skipped on local dev host. Test with Shopify preview or live domain.');
      return;
    }

    // 1. Get currently active Shopify market country.
    const currentCountry = getCurrentShopifyCountry();

    // 2. Detect the visitor's real IP country.
    const visitorCountry = await detectVisitorCountry();
    if (!visitorCountry) {
      console.log('[GBP Auto-Switch] Could not detect visitor country.');
      return;
    }

    // 3. Determine market target by visitor country.
    const targetCountry = getTargetCountry(visitorCountry);
    console.log('[GBP Auto-Switch] Visitor country:', visitorCountry, '| Target market country:', targetCountry);

    // 4. If already in the right market, do nothing.
    if (currentCountry === targetCountry) {
      console.log('[GBP Auto-Switch] Already in target market context, nothing to do.');
      return;
    }

    // 5. Submit the real Shopify localization form → triggers server-side
    //    currency conversion just like a manual country selection.
    submitLocalizationForm(targetCountry);
  }

  // Run after DOM is ready (script is loaded with defer, but be safe).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for debugging in browser console.
  window.ShopifyGBPAutoSwitch = { init, detectVisitorCountry, getCurrentShopifyCountry, getTargetCountry };
})();
